import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, doc, runTransaction, Timestamp, increment, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import { formatDistanceToNow } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faEllipsisV, faReply, faFlag, faEdit, faTimes } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

const SORT_OPTIONS = [
  { value: 'helpful', label: 'Most Helpful' },
  { value: 'recent', label: 'Most Recent' },
];

export default function Comments({ contentId, contentType }) {
  const { user } = useAuth();
  const { family } = useFamily();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState('helpful');
  const [showMenuFor, setShowMenuFor] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [showReportModal, setShowReportModal] = useState(null);
  const [reportReason, setReportReason] = useState('');

  // Load approved comments (top-level only, replies loaded separately)
  useEffect(() => {
    if (!contentId) return;

    const q = query(
      collection(db, 'comments'),
      where('contentId', '==', contentId),
      where('contentType', '==', contentType),
      where('status', '==', 'approved'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Separate top-level comments from replies
        const topLevel = data.filter(c => !c.parentCommentId);
        const replies = data.filter(c => c.parentCommentId);
        
        // Attach replies to their parent comments
        const commentsWithReplies = topLevel.map(comment => ({
          ...comment,
          replies: replies
            .filter(r => r.parentCommentId === comment.id)
            .sort((a, b) => a.createdAt?.toMillis() - b.createdAt?.toMillis())
        }));
        
        setComments(commentsWithReplies);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading comments:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [contentId, contentType]);

  // Get family context string
  const getFamilyContext = () => {
    if (!family) return '';
    const kidsCount = family.kids?.length || 0;
    if (kidsCount === 0) return 'Family';
    const ages = family.kids.map(kid => kid.age).filter(Boolean);
    if (ages.length === 0) return `Family of ${kidsCount}`;
    return `Family of ${kidsCount}${kidsCount > 1 ? ' kids' : ' kid'} (ages ${ages.join(', ')})`;
  };

  // Get user's first name
  const getUserFirstName = () => {
    if (user?.displayName) {
      return user.displayName.split(' ')[0];
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'Anonymous';
  };

  // Handle comment submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please sign in to comment');
      return;
    }

    if (!commentText.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    if (commentText.length > 500) {
      toast.error('Comment must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);

    try {
      await addDoc(collection(db, 'comments'), {
        contentId: contentId,
        contentType: contentType,
        userId: user.uid,
        userName: getUserFirstName(),
        familyContext: getFamilyContext(),
        text: commentText.trim(),
        hearts: 0,
        heartedBy: [],
        status: 'pending',
        reportedBy: [],
        createdAt: Timestamp.now(),
        approvedAt: null,
        approvedBy: null
      });

      setCommentText('');
      toast.success('Comment submitted! It will be visible after approval.');
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error('Failed to submit comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle heart toggle
  const handleHeart = async (commentId, isHearted) => {
    if (!user) {
      toast.error('Please sign in to heart comments');
      return;
    }

    try {
      const commentRef = doc(db, 'comments', commentId);
      
      await runTransaction(db, async (transaction) => {
        const commentDoc = await transaction.get(commentRef);
        if (!commentDoc.exists()) {
          throw new Error('Comment does not exist');
        }

        if (isHearted) {
          // Unheart
          transaction.update(commentRef, {
            hearts: increment(-1),
            heartedBy: arrayRemove(user.uid)
          });
        } else {
          // Heart
          transaction.update(commentRef, {
            hearts: increment(1),
            heartedBy: arrayUnion(user.uid)
          });
        }
      });
    } catch (error) {
      console.error('Error toggling heart:', error);
      toast.error('Failed to update heart');
    }
  };

  // Handle reply submission
  const handleReplySubmit = async (parentCommentId) => {
    if (!user) {
      toast.error('Please sign in to reply');
      return;
    }

    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    if (replyText.length > 500) {
      toast.error('Reply must be 500 characters or less');
      return;
    }

    try {
      await addDoc(collection(db, 'comments'), {
        contentId: contentId,
        contentType: contentType,
        parentCommentId: parentCommentId,
        userId: user.uid,
        userName: getUserFirstName(),
        familyContext: getFamilyContext(),
        text: replyText.trim(),
        hearts: 0,
        heartedBy: [],
        status: 'pending',
        reportedBy: [],
        createdAt: Timestamp.now(),
        approvedAt: null,
        approvedBy: null
      });

      setReplyText('');
      setReplyingTo(null);
      toast.success('Reply submitted! It will be visible after approval.');
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast.error('Failed to submit reply');
    }
  };

  // Handle edit
  const handleEditStart = (comment) => {
    setEditingComment(comment.id);
    setEditText(comment.text);
  };

  const handleEditCancel = () => {
    setEditingComment(null);
    setEditText('');
  };

  const handleEditSave = async (commentId) => {
    if (!editText.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    if (editText.length > 500) {
      toast.error('Comment must be 500 characters or less');
      return;
    }

    try {
      await updateDoc(doc(db, 'comments', commentId), {
        text: editText.trim(),
        editedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      setEditingComment(null);
      setEditText('');
      toast.success('Comment updated');
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    }
  };

  // Handle report
  const handleReport = async (commentId) => {
    if (!user) {
      toast.error('Please sign in to report comments');
      return;
    }

    if (!reportReason.trim()) {
      toast.error('Please provide a reason for reporting');
      return;
    }

    try {
      const commentRef = doc(db, 'comments', commentId);
      await updateDoc(commentRef, {
        reportedBy: arrayUnion(user.uid),
        reportReasons: arrayUnion({
          userId: user.uid,
          reason: reportReason.trim(),
          reportedAt: Timestamp.now()
        })
      });

      setShowReportModal(null);
      setReportReason('');
      toast.success('Comment reported. Thank you for helping keep our community safe.');
    } catch (error) {
      console.error('Error reporting comment:', error);
      toast.error('Failed to report comment');
    }
  };

  // Handle delete (own comment)
  const handleDelete = async (commentId) => {
    if (!user) return;

    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await updateDoc(doc(db, 'comments', commentId), {
        status: 'deleted'
      });
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  // Sort comments (only top-level, replies stay with their parents)
  const sortedComments = [...comments].sort((a, b) => {
    if (sortBy === 'helpful') {
      // Count total hearts including replies
      const aTotalHearts = (a.hearts || 0) + (a.replies?.reduce((sum, r) => sum + (r.hearts || 0), 0) || 0);
      const bTotalHearts = (b.hearts || 0) + (b.replies?.reduce((sum, r) => sum + (r.hearts || 0), 0) || 0);
      return bTotalHearts - aTotalHearts;
    } else {
      return b.createdAt?.toMillis() - a.createdAt?.toMillis();
    }
  });

  return (
    <div className="mt-8 pt-8 border-t border-border">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl sm:text-2xl font-semibold text-text-primary">
          Comments {comments.length > 0 && `(${comments.length + comments.reduce((sum, c) => sum + (c.replies?.length || 0), 0)})`}
        </h2>
        {comments.length > 0 && (
          <>
            <label htmlFor="comment-sort" className="sr-only">
              Sort comments
            </label>
            <select
              id="comment-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              aria-label="Sort comments"
            >
            {SORT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          </>
        )}
      </div>

      {/* Comment Input */}
      {user ? (
        <form onSubmit={handleSubmit} className="mb-8">
          <div className="mb-3">
            <label htmlFor="comment-input" className="sr-only">
              Write a comment
            </label>
            <textarea
              id="comment-input"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Share your thoughts..."
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              aria-describedby="comment-char-count"
            />
            <div className="flex items-center justify-between mt-2">
              <span id="comment-char-count" className="text-xs text-text-secondary" aria-live="polite">
                {commentText.length}/500 characters
              </span>
            </div>
          </div>
          <button
            type="submit"
            disabled={isSubmitting || !commentText.trim()}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Comment'}
          </button>
        </form>
      ) : (
        <div className="mb-8 p-4 bg-surface rounded-lg text-center">
          <p className="text-text-secondary mb-3">Sign in to join the conversation</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-semibold"
          >
            Sign In
          </button>
        </div>
      )}

      {/* Comments List */}
      {loading ? (
        <div className="text-center py-8 text-text-secondary">Loading comments...</div>
      ) : sortedComments.length === 0 ? (
        <div className="text-center py-8 text-text-secondary">
          No comments yet. Be the first to comment!
        </div>
      ) : (
        <div className="space-y-6">
          {sortedComments.map((comment) => {
            const isHearted = user && (comment.heartedBy || []).includes(user.uid);
            const isOwnComment = user && comment.userId === user.uid;

            return (
              <div key={comment.id} className="bg-surface rounded-lg p-4 sm:p-5">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-text-primary">
                        {comment.userName}
                      </span>
                      {comment.familyContext && (
                        <span className="text-xs text-text-secondary bg-white px-2 py-1 rounded-full">
                          {comment.familyContext}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text-secondary mb-2">
                      {comment.createdAt && formatDistanceToNow(comment.createdAt.toDate(), { addSuffix: true })}
                    </p>
                  </div>
                  {isOwnComment && (
                    <div className="relative">
                      <button
                        onClick={() => setShowMenuFor(showMenuFor === comment.id ? null : comment.id)}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        aria-label="Comment options"
                      >
                        <FontAwesomeIcon icon={faEllipsisV} className="w-4 h-4 text-text-secondary" />
                      </button>
                      {showMenuFor === comment.id && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-border rounded-lg shadow-lg z-10 min-w-[120px]">
                          <button
                            onClick={() => {
                              handleEditStart(comment);
                              setShowMenuFor(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            <FontAwesomeIcon icon={faEdit} className="w-3 h-3 mr-2" />
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              handleDelete(comment.id);
                              setShowMenuFor(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Comment text or edit form */}
                {editingComment === comment.id ? (
                  <div className="mb-3">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-text-secondary">
                        {editText.length}/500 characters
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={handleEditCancel}
                          className="px-3 py-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleEditSave(comment.id)}
                          className="px-3 py-1 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-text-primary mb-3 leading-relaxed">
                      {comment.text}
                      {comment.editedAt && (
                        <span className="text-xs text-text-secondary ml-2 italic">(edited)</span>
                      )}
                    </p>
                    
                    {/* Replies */}
                    {comment.replies && comment.replies.length > 0 && (
                      <div className="ml-4 sm:ml-6 pl-4 sm:pl-6 border-l-2 border-border mb-3 space-y-3">
                        {comment.replies.map((reply) => {
                          const isReplyHearted = user && (reply.heartedBy || []).includes(user.uid);
                          const isOwnReply = user && reply.userId === user.uid;
                          
                          return (
                            <div key={reply.id} className="bg-white rounded-lg p-3">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-sm text-text-primary">
                                      {reply.userName}
                                    </span>
                                    {reply.familyContext && (
                                      <span className="text-xs text-text-secondary bg-surface px-2 py-0.5 rounded-full">
                                        {reply.familyContext}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-text-secondary">
                                    {reply.createdAt && formatDistanceToNow(reply.createdAt.toDate(), { addSuffix: true })}
                                    {reply.editedAt && <span className="italic"> • edited</span>}
                                  </p>
                                </div>
                                {isOwnReply && (
                                  <button
                                    onClick={() => {
                                      handleEditStart(reply);
                                    }}
                                    className="p-1 text-xs text-text-secondary hover:text-text-primary"
                                    aria-label="Edit reply"
                                  >
                                    <FontAwesomeIcon icon={faEdit} className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                              {editingComment === reply.id ? (
                                <div>
                                  <textarea
                                    value={editText}
                                    onChange={(e) => setEditText(e.target.value)}
                                    rows={2}
                                    maxLength={500}
                                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                                  />
                                  <div className="flex items-center justify-end gap-2 mt-2">
                                    <button
                                      onClick={handleEditCancel}
                                      className="px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => handleEditSave(reply.id)}
                                      className="px-2 py-1 text-xs bg-primary text-white rounded-lg hover:bg-primary/90"
                                    >
                                      Save
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <p className="text-sm text-text-primary mb-2 leading-relaxed">
                                    {reply.text}
                                  </p>
                                  <button
                                    onClick={() => handleHeart(reply.id, isReplyHearted)}
                                    className={`flex items-center gap-1.5 px-2 py-1 rounded-lg transition-colors text-xs ${
                                      isReplyHearted
                                        ? 'bg-red-50 text-red-600'
                                        : 'bg-surface text-text-secondary hover:bg-gray-100'
                                    }`}
                                    disabled={!user}
                                  >
                                    <FontAwesomeIcon 
                                      icon={faHeart} 
                                      className={`w-3 h-3 ${isReplyHearted ? 'text-red-600' : ''}`}
                                    />
                                    <span>{reply.hearts || 0}</span>
                                  </button>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
                
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => handleHeart(comment.id, isHearted)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                      isHearted
                        ? 'bg-red-50 text-red-600'
                        : 'bg-white text-text-secondary hover:bg-gray-100'
                    }`}
                    disabled={!user}
                    aria-label={isHearted ? `Unheart comment by ${comment.userName}` : `Heart comment by ${comment.userName}`}
                  >
                    <FontAwesomeIcon 
                      icon={faHeart} 
                      className={`w-4 h-4 ${isHearted ? 'text-red-600' : ''}`}
                    />
                    <span className="text-sm font-medium">
                      {comment.hearts || 0}
                    </span>
                  </button>
                  
                  {user && !isOwnComment && (
                    <button
                      onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white text-text-secondary hover:bg-gray-100 transition-colors"
                      aria-label={`Reply to ${comment.userName}`}
                    >
                      <FontAwesomeIcon icon={faReply} className="w-4 h-4" />
                      <span className="text-sm">Reply</span>
                      {comment.replies && comment.replies.length > 0 && (
                        <span className="text-xs">({comment.replies.length})</span>
                      )}
                    </button>
                  )}
                  
                  {user && !isOwnComment && (
                    <button
                      onClick={() => setShowReportModal(comment.id)}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white text-text-secondary hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      aria-label={`Report comment by ${comment.userName}`}
                    >
                      <FontAwesomeIcon icon={faFlag} className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* Reply form */}
                {replyingTo === comment.id && (
                  <div className="mt-4 ml-4 sm:ml-6 pl-4 sm:pl-6 border-l-2 border-primary">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={`Reply to ${comment.userName}...`}
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span id={`reply-char-count-${comment.id}`} className="text-xs text-text-secondary" aria-live="polite">
                        {replyText.length}/500 characters
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyText('');
                          }}
                          className="px-3 py-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleReplySubmit(comment.id)}
                          disabled={!replyText.trim()}
                          className="px-3 py-1 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Reply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary">Report Comment</h3>
              <button
                onClick={() => {
                  setShowReportModal(null);
                  setReportReason('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5 text-text-secondary" />
              </button>
            </div>
            <p className="text-sm text-text-secondary mb-4">
              Please tell us why you're reporting this comment. This helps us keep our community safe.
            </p>
            <textarea
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              placeholder="Reason for reporting..."
              rows={4}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none mb-4"
            />
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowReportModal(null);
                  setReportReason('');
                }}
                className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReport(showReportModal)}
                disabled={!reportReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

